import { fetchCall, fetchLoginCall } from "../ajax";
import { API_CONSTANTS, API_METHODS } from "../api-constant";
import { clearSession, getLoginPath } from "../../services/auth";

const LOGOUT_TIMEOUT_MS = 10000;

export const UserLogin = async (payload) => {
  const url = `${API_CONSTANTS.LOGIN}`;
  return fetchCall(url, API_METHODS.POST, payload);
};

// Authenticated with refresh enabled, so an expired access token still gets
// the BE session invalidated. The timeout stops a hung request from keeping
// the session open on this device.
export const UserLogout = async () =>
  fetchCall(API_CONSTANTS.LOGOUT, API_METHODS.POST, undefined, {
    skipAuth: false,
    skipRefresh: false,
    timeout: LOGOUT_TIMEOUT_MS,
  });

let logoutInFlight = null;

// The single logout path for all login types (manual and idle). The BE picks
// the provider: Entra sessions get a logoutUrl, direct HP / Super Admin don't.
// Full page load on purpose, so in-memory user state doesn't survive logout.
export const logoutAndRedirect = () => {
  if (!logoutInFlight) {
    logoutInFlight = (async () => {
      // Read first: clearSession wipes the loginType this depends on.
      const loginPath = getLoginPath();
      let logoutUrl;
      try {
        const res = await UserLogout();
        logoutUrl = res?.data?.logoutUrl;
      } catch {
        // Session already dead or BE unreachable: still sign out locally.
      }
      clearSession();
      window.location.replace(logoutUrl || loginPath);
    })().finally(() => {
      logoutInFlight = null;
    });
  }
  return logoutInFlight;
};

export const UserMfaOtp = async (payload) => {
  const url = `${API_CONSTANTS.MFA_OTP}`;
  return fetchCall(url, API_METHODS.POST, payload);
};

// Trades the one-time SSO code for the same session payload as MFA OTP.
// withCredentials: BE checks the entra_binding cookie set during /entra/login.
export const EntraExchange = async (code) =>
  fetchCall(API_CONSTANTS.ENTRA_EXCHANGE, API_METHODS.POST, { code }, { withCredentials: true });

export const UserForgotPassword = async (payload) => {
  const url = `${API_CONSTANTS.FORGOT_PASSWORD}`;
  return fetchCall(url, API_METHODS.POST, payload);
};

export const UserVerifyOTP = async (payload, email, authenticationType) => {
  const url = `${API_CONSTANTS.VERIFY_OTP}`;

  // If authenticationType is provided, send all parameters as query params
  if (authenticationType !== undefined) {
    const queryString = `?otp=${payload?.otp}&authenticationType=${authenticationType}&email=${email}`;
    return fetchLoginCall(url + queryString, API_METHODS.POST);
  }

  // Otherwise use query string method (original behavior for forgot password)
  const queryString = `?otp=${payload?.otp}&email=${email}`;
  return fetchCall(url + queryString, API_METHODS.POST);
};

export const RequestOtp = async (payload) => {
  const url = `${API_CONSTANTS.REQUEST_OTP}`;
  return fetchLoginCall(url, API_METHODS.GET, payload);
};

export const UserPasswordReset = async (payload) => {
  const url = `${API_CONSTANTS.PASSWORD_RESET}`;
  return fetchCall(url, API_METHODS.POST, payload);
};

export const CurrentUser = async () => {
  const url = `${API_CONSTANTS.CURRENT_USER}`;
  return fetchLoginCall(url, API_METHODS.GET);
};

export const CheckEmailAvailability = async (email) => {
  const url = `${API_CONSTANTS.CHECK_EMAIL_AVAILABILITY}?email=${encodeURIComponent(email)}`;
  return fetchCall(url, API_METHODS.GET);
};

export const UserRegister = async (payload) => {
  const url = `${API_CONSTANTS.REGISTER}`;
  return fetchCall(url, API_METHODS.POST, payload);
};
